/**
 * The pigment bench, and the naming of colours.
 *
 * Names are the difference between "a teal swatch" and "Deep Lagoon". They
 * give the day a handle — something to say out loud — so every target and
 * every pigment carries one.
 */

import { Lab, deltaE, hexToOklab, labToLch } from './colour';
import { Rng, shuffled } from './rng';

export interface Pigment {
  id: string;
  name: string;
  hex: string;
  lab: Lab;
}

export interface Source extends Pigment {
  /** Hue family word, for screen readers. */
  family: string;
}

interface PigmentSeed {
  id: string;
  name: string;
  hex: string;
}

/** Real artist's pigments. Chosen for hue spread, not for chemistry. */
const PIGMENT_SEEDS: readonly PigmentSeed[] = [
  { id: 'titanium-white', name: 'Titanium White', hex: '#F6F4EC' },
  { id: 'naples-yellow', name: 'Naples Yellow', hex: '#EFD189' },
  { id: 'cadmium-yellow', name: 'Cadmium Yellow', hex: '#FFCC00' },
  { id: 'indian-yellow', name: 'Indian Yellow', hex: '#E79E1C' },
  { id: 'cadmium-orange', name: 'Cadmium Orange', hex: '#F76B15' },
  { id: 'vermilion', name: 'Vermilion', hex: '#DF331C' },
  { id: 'crimson-lake', name: 'Crimson Lake', hex: '#C11848' },
  { id: 'rose-madder', name: 'Rose Madder', hex: '#DD5B79' },
  { id: 'quinacridone', name: 'Quinacridone Rose', hex: '#CE1E84' },
  { id: 'dioxazine-violet', name: 'Dioxazine Violet', hex: '#5A2A9D' },
  { id: 'cobalt-violet', name: 'Cobalt Violet', hex: '#8A55C9' },
  { id: 'ultramarine', name: 'Ultramarine', hex: '#2440B7' },
  { id: 'cobalt-blue', name: 'Cobalt Blue', hex: '#1E6ECF' },
  { id: 'cerulean', name: 'Cerulean', hex: '#1FA2C8' },
  { id: 'viridian', name: 'Viridian', hex: '#12876B' },
  { id: 'sap-green', name: 'Sap Green', hex: '#5D9E29' },
  { id: 'terra-verte', name: 'Terra Verte', hex: '#4E7A44' },
  { id: 'burnt-sienna', name: 'Burnt Sienna', hex: '#A35229' },
  { id: 'raw-umber', name: 'Raw Umber', hex: '#6A5637' },
  { id: 'paynes-grey', name: "Payne's Grey", hex: '#41566B' },
  { id: 'ivory-black', name: 'Ivory Black', hex: '#1E1B18' },
];

export const ALL_PIGMENTS: readonly Pigment[] = PIGMENT_SEEDS.map((s) => ({
  ...s,
  lab: hexToOklab(s.hex),
}));

const HUE_SECTORS = 12;
const NEUTRAL_CUTOFF = 0.03;

function sectorOf(p: Pigment): number {
  const { C, h } = labToLch(p.lab);
  if (C < NEUTRAL_CUTOFF) return -1;
  return Math.min(HUE_SECTORS - 1, Math.floor((h / 360) * HUE_SECTORS));
}

/**
 * Pick a bench of `count` pigments that covers the widest useful ground.
 *
 * Hue comes first and distance second. Plain farthest-point selection drifts
 * toward yellow and red, because OKLab distances are simply larger out there —
 * so a naive bench is two yellows, three reds and a token blue. Taking one
 * pigment per hue sector before anything else, then filling the remainder by
 * farthest-point, keeps the whole wheel on the table.
 */
export function chooseBench(rng: Rng, count: number, minSeparation = 8): Pigment[] {
  const pool = shuffled(ALL_PIGMENTS, rng);
  if (count >= pool.length) return pool.slice(0, count);

  const bySector = new Map<number, Pigment[]>();
  for (const p of pool) {
    const s = sectorOf(p);
    (bySector.get(s) ?? bySector.set(s, []).get(s)!).push(p);
  }

  const chosen: Pigment[] = [];
  const remaining = pool.slice();

  // One per hue sector, walk the wheel in a random rotation so consecutive
  // days do not start on the same colour.
  const sectors = shuffled([...bySector.keys()], rng);
  for (const s of sectors) {
    if (chosen.length >= count) break;
    const candidates = bySector.get(s)!;
    const take = candidates[Math.floor(rng() * candidates.length)];
    chosen.push(take);
    remaining.splice(remaining.indexOf(take), 1);
  }

  while (chosen.length < count && remaining.length > 0) {
    // Score every candidate by its nearest neighbour on the bench.
    const scored = remaining.map((p) => ({
      p,
      score: Math.min(...chosen.map((c) => deltaE(c.lab, p.lab))),
    }));
    scored.sort((x, y) => y.score - x.score);

    // Take one of the three furthest, so benches vary without collapsing
    // into the same maximal-spread set every time.
    const take = scored[Math.min(scored.length - 1, Math.floor(rng() * 3))];
    chosen.push(take.p);
    remaining.splice(remaining.indexOf(take.p), 1);
  }

  return finaliseBench(chosen, remaining, minSeparation, rng);
}

/**
 * Enforce minimum separation, then order the bench as a spectrum.
 *
 * Two pigments that look alike make one slide a dead decision, so clashing
 * pairs are swapped out for the widest available replacement.
 */
function finaliseBench(
  chosen: Pigment[],
  remaining: Pigment[],
  minSeparation: number,
  rng: Rng,
): Pigment[] {
  for (let pass = 0; pass < 40; pass++) {
    const clash = closestPair(chosen);
    if (!clash || clash.d >= minSeparation) break;
    const swapIn = remaining
      .filter((p) => chosen.every((c) => deltaE(c.lab, p.lab) >= minSeparation))
      .sort((x, y) => spreadScore(y.lab, chosen) - spreadScore(x.lab, chosen))[0];
    if (!swapIn) break;
    chosen.splice(chosen.indexOf(clash.b), 1, swapIn);
    remaining.splice(remaining.indexOf(swapIn), 1);
    remaining.push(clash.b);
  }
  void rng;

  // Stable ordering so the bench reads as a spectrum, not a shuffle.
  return chosen.sort((a, b) => labToLch(a.lab).h - labToLch(b.lab).h);
}

/**
 * A bench built around a target hue.
 *
 * Hue-first selection alone still under-serves green and cyan, because those
 * hues have less chroma headroom and so lose every saturation contest to a
 * yellow. Anchoring part of the bench on the day's intended hue guarantees
 * the target's family is physically on the bench, then the remainder is
 * filled by farthest-point so the bench still spans the wheel and the puzzle
 * still has distractors.
 */
export function chooseBenchAimed(
  rng: Rng,
  count: number,
  aimHue: number,
  anchors = 2,
  anchorShift = 0,
  minSeparation = 8,
): Pigment[] {
  if (count >= ALL_PIGMENTS.length) return shuffled(ALL_PIGMENTS, rng).slice(0, count);

  const remaining = shuffled(ALL_PIGMENTS, rng);
  const bySector = new Map<number, Pigment[]>();
  for (const p of remaining) {
    const s = sectorOf(p);
    const list = bySector.get(s);
    if (list) list.push(p);
    else bySector.set(s, [p]);
  }

  const chosen: Pigment[] = [];
  const take = (p: Pigment) => {
    chosen.push(p);
    const i = remaining.indexOf(p);
    if (i >= 0) remaining.splice(i, 1);
  };

  const sectorSize = 360 / HUE_SECTORS;
  const aimSector = Math.min(HUE_SECTORS - 1, Math.floor((((aimHue % 360) + 360) % 360) / sectorSize));

  // 1. Anchor near the aim: the aim's sector and its neighbours, starting
  //    `anchorShift` sectors out so the bench is not sitting *on* the target.
  const offsets = [0, 1, -1, 2, -2, 3, -3].map((o) => o + anchorShift);
  for (const offset of offsets) {
    if (chosen.length >= anchors) break;
    const s = (((aimSector + offset) % HUE_SECTORS) + HUE_SECTORS) % HUE_SECTORS;
    const cands = (bySector.get(s) ?? []).filter((p) => remaining.includes(p));
    if (cands.length === 0) continue;
    take(cands[Math.floor(rng() * cands.length)]);
  }

  // 2. Fill the rest by farthest-point, so the bench still spans the wheel.
  while (chosen.length < count && remaining.length > 0) {
    const scored = remaining.map((p) => ({
      p,
      score: Math.min(...chosen.map((c) => deltaE(c.lab, p.lab))),
    }));
    scored.sort((x, y) => y.score - x.score);
    take(scored[Math.min(scored.length - 1, Math.floor(rng() * 3))].p);
  }

  return finaliseBench(chosen, remaining, minSeparation, rng);
}

function closestPair(items: readonly Pigment[]): { a: Pigment; b: Pigment; d: number } | null {
  let best: { a: Pigment; b: Pigment; d: number } | null = null;
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const d = deltaE(items[i].lab, items[j].lab);
      if (!best || d < best.d) best = { a: items[i], b: items[j], d };
    }
  }
  return best;
}

function spreadScore(candidate: Lab, against: readonly Pigment[]): number {
  return Math.min(...against.map((c) => deltaE(c.lab, candidate)));
}

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/** Boundaries match `hueFamily` in colour.ts, which is OKLab-aligned. */
const HUE_NOUNS: ReadonlyArray<readonly [number, readonly string[]]> = [
  [16, ['Rose', 'Blush', 'Coral']],
  [42, ['Crimson', 'Madder', 'Cochineal']],
  [68, ['Vermilion', 'Ember', 'Cinnabar']],
  [92, ['Saffron', 'Amber', 'Ochre']],
  [118, ['Lime', 'Chartreuse', 'Orchard']],
  [136, ['Green', 'Fern', 'Verdant']],
  [162, ['Jade', 'Viridian', 'Malachite']],
  [190, ['Cyan', 'Lagoon', 'Tide']],
  [215, ['Cerulean', 'Harbour', 'Azure']],
  [238, ['Azure', 'Cornflower', 'Sky']],
  [262, ['Blue', 'Cobalt', 'Indigo']],
  [290, ['Violet', 'Iris', 'Amethyst']],
  [315, ['Magenta', 'Fuchsia', 'Peony']],
  [361, ['Rose', 'Blush', 'Coral']],
];

const NEUTRAL_NOUNS = ['Shadow', 'Ash', 'Slate', 'Bone', 'Mist', 'Salt', 'Chalk'];

/**
 * A stable, evocative name for a colour. Same colour, same name, always.
 */
export function nameForLab(lab: Lab): string {
  const { L, C, h } = labToLch(lab);
  const variant = Math.floor((h % 21) / 7) % 3;

  if (C < 0.028) {
    const noun = NEUTRAL_NOUNS[Math.min(NEUTRAL_NOUNS.length - 1, Math.floor(L * 6))];
    return `${neutralAdjective(L)} ${noun}`;
  }

  const band = HUE_NOUNS.find(([max]) => h < max) ?? HUE_NOUNS[HUE_NOUNS.length - 1];
  const noun = band[1][variant % band[1].length];
  return `${chromaticAdjective(L, C, h)} ${noun}`;
}

function neutralAdjective(L: number): string {
  if (L < 0.22) return 'Dead';
  if (L < 0.4) return 'Smoked';
  if (L < 0.62) return 'Dusty';
  if (L < 0.8) return 'Faded';
  return 'Quiet';
}

function chromaticAdjective(L: number, C: number, h: number): string {
  const v = Math.floor((h % 40) / 20) % 2;
  if (C < 0.055) return ['Muted', 'Dusty'][v];
  if (L < 0.24) return ['Deep', 'Buried'][v];
  if (L < 0.38) return ['Dark', 'Old'][v];
  if (L < 0.56) return ['True', 'Steady'][v];
  if (L < 0.72) return ['Soft', 'Wild'][v];
  if (L < 0.87) return ['Bright', 'Light'][v];
  return ['Brilliant', 'Blazing'][v];
}
